import type { ChallengeHud } from "../game/Game";
import { challengeHint } from "../game/challenge";
import { PRIMARY, SECONDARY } from "./buttons";
import { LetterTiles, lettersGot, letterTitle } from "./LetterTiles";
import { KeyHint } from "./KeyHint";
import { buttonFor, type InputKind } from "./input-names";
import { DIALOG, SCRIM, TITLE } from "./theme";

const clock = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

/**
 * A free-skate challenge's cards (#47): the start card (the goal, how to do
 * it, the time; X goes, O backs out) and the pass / fail card (X again, O
 * back to free skate, and on a map with hubs △ back to the hub, #86).
 */
export function ChallengeCard({
  challenge,
  onGo,
  onRetry,
  onLeave,
  onHub,
  input = "keyboard",
}: {
  challenge: ChallengeHud;
  onGo: () => void;
  onRetry: () => void;
  onLeave: () => void;
  /** Back to the challenge's hub (maps with hubs only). */
  onHub?: () => void;
  /** The input you're using: its buttons are the ones named (#147). */
  input?: InputKind;
}) {
  const ready = challenge.phase === "ready";
  const passed = challenge.phase === "passed";
  const title = ready ? "Challenge" : passed ? "Challenge complete!" : "Challenge failed";
  return (
    <div className={`absolute inset-0 grid place-items-center ${SCRIM} p-4`}>
      <div
        role="dialog"
        aria-label={title}
        className={`${DIALOG} max-h-[calc(100dvh-2rem)] w-[min(90vw,26rem)] overflow-y-auto text-center ${passed ? "outline-4 outline-aqua" : ""}`}
      >
        <h2 className={`${TITLE} text-3xl`}>{title}</h2>
        <p className="mt-2 text-xl font-black uppercase italic">{letterTitle(challenge.id, challenge.text)}</p>
        {challenge.progress !== null && (
          <div className="mt-2 flex justify-center">
            <LetterTiles id={challenge.id} progress={challenge.progress} size="lg" />
          </div>
        )}
        {ready ? (
          <>
            <p className="mt-2 text-sm font-semibold">{challengeHint(challenge.id, challenge.kind)}</p>
            <p className="mt-2 text-4xl font-black italic tabular-nums">{clock(challenge.time)}</p>
          </>
        ) : (
          <p className="mt-2 text-sm font-bold">
            {passed
              ? `With ${clock(challenge.seconds)} to spare. It's checked off in the Challenges list.`
              : challenge.kind === "score"
                ? `${challenge.score.toLocaleString()} points. So close.`
                : challenge.progress
                  ? `Got ${lettersGot(challenge.progress).filter(Boolean).length} of 5. Have another go.`
                  : "Time's up. Have another go."}
          </p>
        )}
        <div className="mt-4 flex flex-col gap-2">
          <button
            type="button"
            autoFocus
            onClick={ready ? onGo : onRetry}
            className={`${PRIMARY} w-full px-4 py-2 text-lg`}
          >
            {ready ? "Go!" : "Try again"} <KeyHint input={input} button="✕" />
          </button>
          <button
            type="button"
            onClick={onLeave}
            className={`${SECONDARY} w-full px-4 py-2 text-lg`}
          >
            {ready ? "Not now" : "Free skate"} <KeyHint input={input} button="○" />
          </button>
          {!ready && onHub && (
            <button type="button" onClick={onHub} className={`${SECONDARY} w-full px-4 py-2 text-lg`}>
              Back to hub <KeyHint input={input} button="△" />
            </button>
          )}
        </div>
        {ready && <p className="mt-3 text-xs font-semibold opacity-70">{buttonFor(input, "Start")} pauses: retry or quit from there</p>}
      </div>
    </div>
  );
}
